import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { authCacheScope, listWorkspaces, workspaceKeys } from "@rekonas/data-access";
import type { WorkspaceSummary } from "@rekonas/domain";

export function useOperationalWorkspace(
  requestedWorkspaceRef?: string,
  acceptedPermissions: readonly WorkspaceSummary["permissions"][number][] = ["operational_recordings.view"],
) {
  const query = useQuery({ queryKey: workspaceKeys.all(), queryFn: ({ signal }) => listWorkspaces(signal) });
  const authorized = (query.data || []).filter((workspace) =>
    acceptedPermissions.some((permission) => workspace.permissions.includes(permission)),
  );
  const storageKey = `neoma.operational-workspace.v1:${encodeURIComponent(authCacheScope())}`;
  const [stored, setStored] = useState(() => localStorage.getItem(storageKey) || "");
  useEffect(() => { setStored(localStorage.getItem(storageKey) || ""); }, [storageKey]);
  const workspace = useMemo(() => {
    if (requestedWorkspaceRef) return authorized.find((item) => item.slug === requestedWorkspaceRef) || null;
    if (stored) return authorized.find((item) => item.slug === stored) || null;
    return authorized.length === 1 ? authorized[0] : null;
  }, [authorized, requestedWorkspaceRef, stored]);
  const selectWorkspace = (workspaceRef: string) => {
    if (!authorized.some((item) => item.slug === workspaceRef)) return;
    localStorage.setItem(storageKey, workspaceRef);
    setStored(workspaceRef);
  };
  return { ...query, workspace, workspaces: authorized, selectWorkspace,
    authorizedCount: authorized.length };
}
